import { create } from 'zustand'

/**
 * Med request page sections a prescriber can pin from the navigation menu. Pinned
 * sections move to the top of the page (below the header), in the page's regular order.
 */
export type PinnableSection =
  | 'patient-data'
  | 'safeguarding-questions'
  | 'previous-orders'
  | 'previous-investigations'
  | 'on-hold-history'
  | 'consultation'
  | 'patient-files'
  | 'scr-data'
  | 'prescription-details'
  | 'additional-notes'

interface PinnedSectionsStore {
  /** Pinned sections per med request id. In memory only — never persisted across sessions. */
  pinnedByMedRequest: Record<string, PinnableSection[]>
  togglePinnedSection: (medRequestId: string, section: PinnableSection) => void
}

export const usePinnedSectionsStore = create<PinnedSectionsStore>(set => ({
  pinnedByMedRequest: {},
  togglePinnedSection: (medRequestId, section) =>
    set(state => {
      const pinned = state.pinnedByMedRequest[medRequestId] ?? []
      const next = pinned.includes(section) ? pinned.filter(item => item !== section) : [...pinned, section]
      return { pinnedByMedRequest: { ...state.pinnedByMedRequest, [medRequestId]: next } }
    })
}))

/** Pinned items first and the rest after, each group keeping its regular order. */
export const orderPinnedFirst = <T>(items: T[], isPinned: (item: T) => boolean) => [
  ...items.filter(isPinned),
  ...items.filter(item => !isPinned(item))
]

const NO_PINS: PinnableSection[] = []

export const usePinnedSections = (medRequestId?: string) =>
  usePinnedSectionsStore(state => (medRequestId ? (state.pinnedByMedRequest[medRequestId] ?? NO_PINS) : NO_PINS))
